import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation } from 'react-router';
import {
  LayoutDashboard,
  GitBranch,
  FileText,
  AlertTriangle,
  BarChart3,
  Building2,
  Users,
  Network,
  Shield,
  GraduationCap,
  Wrench,
  ClipboardCheck,
  BookOpen,
  FileCheck,
  Compass,
  Truck,
  Search,
  RefreshCw,
  MessageSquareWarning,
  Bot,
  FileBarChart,
  Settings,
  ChevronsLeft,
  ChevronsRight,
  LogOut,
  Lock,
} from 'lucide-react';
import { cn } from '@/shared/lib/utils';
import { useUIStore } from '@/shared/stores/uiStore';
import { useOrganization } from '@/shared/hooks/useOrganization';
import { useAuth } from '@/shared/hooks/useAuth';
import { useDashboardStats } from '@/features/dashboard/hooks/useDashboard';
import { Avatar, AvatarImage, AvatarFallback } from '@/shared/components/ui/avatar';
import {
  Sheet,
  SheetContent,
  SheetTitle,
} from '@/shared/components/ui/sheet';
import { ROUTES } from '@/shared/config/routes';
import type { LucideIcon } from 'lucide-react';
import type { DashboardStats } from '@/features/dashboard/types/dashboard.types';

interface NavItem {
  label: string;
  icon: LucideIcon;
  path: string;
  implemented?: boolean;
  featureFlag?: keyof ReturnType<typeof useOrganization>['featureFlags'];
  badgeKey?: keyof Pick<DashboardStats, 'openNcrs' | 'pendingDocuments' | 'openComplaints' | 'pendingChanges' | 'overdueCalibrations'>;
}

interface NavSection {
  title: string;
  items: NavItem[];
}

function getInitials(name: string | null | undefined): string {
  if (!name) return '?';
  return name
    .split(' ')
    .map((n) => n[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

const NAV_SECTIONS: NavSection[] = [
  {
    title: 'nav.sections.overview',
    items: [
      { label: 'nav.dashboard', icon: LayoutDashboard, path: ROUTES.DASHBOARD, implemented: true },
    ],
  },
  {
    title: 'nav.sections.core_quality',
    items: [
      { label: 'nav.processes', icon: GitBranch, path: ROUTES.PROCESSES, implemented: true },
      { label: 'nav.documents', icon: FileText, path: ROUTES.DOCUMENTS, implemented: true, badgeKey: 'pendingDocuments' },
      { label: 'nav.quality_issues', icon: AlertTriangle, path: ROUTES.QUALITY_ISSUES, implemented: true, badgeKey: 'openNcrs' },
      { label: 'nav.complaints', icon: MessageSquareWarning, path: ROUTES.COMPLAINTS, implemented: true, featureFlag: 'complaints_enabled', badgeKey: 'openComplaints' },
      { label: 'nav.kpis', icon: BarChart3, path: ROUTES.KPIS, implemented: true },
    ],
  },
  {
    title: 'nav.sections.organization',
    items: [
      { label: 'nav.org_structure', icon: Building2, path: ROUTES.ORG_STRUCTURE, implemented: true },
      { label: 'nav.staff_directory', icon: Users, path: ROUTES.STAFF, implemented: true },
      { label: 'nav.org_chart', icon: Network, path: ROUTES.ORG_CHART, implemented: true },
    ],
  },
  {
    title: 'nav.sections.compliance',
    items: [
      { label: 'nav.risk_register', icon: Shield, path: ROUTES.RISKS, implemented: true },
      { label: 'nav.training', icon: GraduationCap, path: ROUTES.TRAINING_COURSES, implemented: true },
      { label: 'nav.equipment', icon: Wrench, path: ROUTES.EQUIPMENT, implemented: true, badgeKey: 'overdueCalibrations' },
      { label: 'nav.audits', icon: ClipboardCheck, path: ROUTES.AUDITS, implemented: true },
      { label: 'nav.management_reviews', icon: BookOpen, path: ROUTES.MANAGEMENT_REVIEWS, implemented: true },
      { label: 'nav.qms_planning', icon: FileCheck, path: ROUTES.QMS_PLANNING, implemented: true },
      {
        label: 'nav.design_dev',
        icon: Compass,
        path: ROUTES.DESIGN_DEV,
        implemented: true,
        featureFlag: 'design_dev_enabled',
      },
    ],
  },
  {
    title: 'nav.sections.operations',
    items: [
      { label: 'nav.suppliers', icon: Truck, path: ROUTES.SUPPLIERS, implemented: true },
      { label: 'nav.inspections', icon: Search, path: ROUTES.INSPECTIONS, implemented: true },
      { label: 'nav.change_management', icon: RefreshCw, path: ROUTES.CHANGE_MANAGEMENT, implemented: true, badgeKey: 'pendingChanges' },
    ],
  },
  {
    title: 'nav.sections.intelligence',
    items: [
      { label: 'nav.lessons_learned', icon: BookOpen, path: ROUTES.LESSONS_LEARNED, implemented: true },
      { label: 'nav.ai_assistant', icon: Bot, path: ROUTES.AI_ASSISTANT, implemented: true },
    ],
  },
  {
    title: 'nav.sections.system',
    items: [
      { label: 'nav.reports', icon: FileBarChart, path: ROUTES.REPORTS, implemented: true },
      { label: 'nav.settings', icon: Settings, path: ROUTES.SETTINGS.ROOT, implemented: true },
    ],
  },
];

/** Renders the navigation content — shared between desktop aside and mobile Sheet */
function SidebarContent({ collapsed, onNavigate }: { collapsed: boolean; onNavigate?: () => void }) {
  const { t } = useTranslation();
  const location = useLocation();
  const toggleSidebar = useUIStore((s) => s.toggleSidebar);
  const { featureFlags } = useOrganization();
  const { user, signOut } = useAuth();
  const { data: stats } = useDashboardStats();

  return (
    <>
      {/* Logo / Brand */}
      <div className="flex h-14 items-center border-b border-border px-4">
        {collapsed ? (
          <>
            <img
              src="/logo-icon.svg"
              alt={t('app.name')}
              className="mx-auto h-8 w-8 dark:hidden"
            />
            <img
              src="/logo-icon-white.svg"
              alt={t('app.name')}
              className="mx-auto hidden h-8 w-8 dark:block"
            />
          </>
        ) : (
          <>
            <img
              src="/logo-horizontal.svg"
              alt={t('app.name')}
              className="h-7 dark:hidden"
            />
            <img
              src="/logo-horizontal-white.svg"
              alt={t('app.name')}
              className="hidden h-7 dark:block"
            />
          </>
        )}
        {/* Collapse toggle — desktop only */}
        {!onNavigate && (
          <button
            type="button"
            onClick={toggleSidebar}
            className={cn(
              'flex h-8 w-8 items-center justify-center rounded-md text-sidebar-foreground/60 hover:bg-sidebar-accent hover:text-sidebar-foreground',
              collapsed ? 'mx-auto' : 'ml-auto',
            )}
            aria-label={collapsed ? t('actions.expand') : t('actions.collapse')}
          >
            {collapsed ? (
              <ChevronsRight className="h-4 w-4" />
            ) : (
              <ChevronsLeft className="h-4 w-4" />
            )}
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav className="dark-scroll flex-1 overflow-y-auto p-2">
        {NAV_SECTIONS.map((section) => {
          const visibleItems = section.items.filter((item) => {
            if (item.featureFlag) {
              return featureFlags[item.featureFlag];
            }
            return true;
          });

          if (visibleItems.length === 0) return null;

          return (
            <div key={section.title} className="mb-6">
              {!collapsed && (
                <p className="mb-2 px-3 text-xs font-semibold uppercase tracking-wider text-sidebar-foreground/70">
                  {t(section.title)}
                </p>
              )}
              <div className="space-y-1">
                {visibleItems.map((item) => {
                  const Icon = item.icon;
                  const isImplemented = item.implemented !== false && item.implemented !== undefined;
                  const isActive = isImplemented && (
                    location.pathname === item.path ||
                    (item.path !== '/' && location.pathname.startsWith(item.path))
                  );
                  const badgeCount = item.badgeKey ? stats?.[item.badgeKey] ?? 0 : 0;

                  if (!isImplemented) {
                    return (
                      <div
                        key={item.path}
                        className={cn(
                          'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium cursor-not-allowed select-none',
                          'text-sidebar-foreground/60',
                          collapsed && 'relative justify-center px-0',
                        )}
                        title={collapsed ? t(item.label) : t('nav.coming_soon')}
                      >
                        <Icon className="h-5 w-5 shrink-0" strokeWidth={1.5} />
                        {!collapsed && (
                          <>
                            <span>{t(item.label)}</span>
                            <Lock className="ml-auto h-3.5 w-3.5 shrink-0" strokeWidth={1.5} />
                          </>
                        )}
                      </div>
                    );
                  }

                  return (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      onClick={onNavigate}
                      className={cn(
                        'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                        isActive
                          ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                          : 'text-sidebar-foreground/70 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground',
                        collapsed && 'relative justify-center px-0',
                      )}
                      title={collapsed ? t(item.label) : undefined}
                    >
                      <Icon className="h-5 w-5 shrink-0" strokeWidth={1.5} />
                      {!collapsed && <span>{t(item.label)}</span>}
                      {!collapsed && item.badgeKey && badgeCount > 0 && (
                        <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-warning text-[10px] font-bold text-black">
                          {badgeCount > 99 ? '99+' : badgeCount}
                        </span>
                      )}
                      {collapsed && item.badgeKey && badgeCount > 0 && (
                        <span className="absolute right-1 top-1 h-2 w-2 rounded-full bg-warning" />
                      )}
                    </NavLink>
                  );
                })}
              </div>
            </div>
          );
        })}
      </nav>

      {/* User section */}
      <div className="border-t border-sidebar-foreground/10 p-3">
        <div className={cn(
          'flex items-center gap-3 rounded-md px-2 py-2',
          collapsed && 'justify-center px-0',
        )}>
          <Avatar className="h-8 w-8 shrink-0">
            <AvatarImage src={user?.avatar_url ?? undefined} alt={user?.full_name ?? ''} />
            <AvatarFallback className="bg-sidebar-accent text-sidebar-accent-foreground text-xs">
              {getInitials(user?.full_name)}
            </AvatarFallback>
          </Avatar>
          {!collapsed && (
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-sidebar-foreground">
                {user?.full_name ?? t('user_menu.guest')}
              </p>
              <p className="truncate text-xs text-sidebar-foreground/70">{user?.email}</p>
            </div>
          )}
          {!collapsed && (
            <button
              type="button"
              onClick={() => { void signOut(); }}
              className="shrink-0 rounded p-1.5 text-sidebar-foreground/50 hover:bg-sidebar-accent hover:text-sidebar-foreground"
              aria-label={t('user_menu.logout')}
            >
              <LogOut className="h-4 w-4" strokeWidth={1.5} />
            </button>
          )}
        </div>
      </div>
    </>
  );
}

export function Sidebar() {
  const { t } = useTranslation();
  const location = useLocation();
  const collapsed = useUIStore((s) => s.sidebarCollapsed);
  const mobileSidebarOpen = useUIStore((s) => s.mobileSidebarOpen);
  const setMobileSidebarOpen = useUIStore((s) => s.setMobileSidebarOpen);

  // Close mobile sidebar on route change
  useEffect(() => {
    setMobileSidebarOpen(false);
  }, [location.pathname, setMobileSidebarOpen]);

  return (
    <>
      {/* Desktop sidebar */}
      <aside
        className={cn(
          'hidden h-screen shrink-0 flex-col border-r border-border bg-sidebar transition-[width] duration-200 lg:flex',
          collapsed ? 'w-16' : 'w-64',
        )}
      >
        <SidebarContent collapsed={collapsed} />
      </aside>

      {/* Mobile sidebar drawer */}
      <Sheet open={mobileSidebarOpen} onOpenChange={setMobileSidebarOpen}>
        <SheetContent
          side="left"
          className="flex flex-col bg-sidebar p-0 text-sidebar-foreground lg:hidden"
          aria-describedby={undefined}
        >
          <SheetTitle className="sr-only">{t('actions.toggle_menu')}</SheetTitle>
          <SidebarContent
            collapsed={false}
            onNavigate={() => { setMobileSidebarOpen(false); }}
          />
        </SheetContent>
      </Sheet>
    </>
  );
}
